import axios from 'axios'
import { Document, Analysis, Student, CreateStudentRequest, UpdateStudentRequest } from '../types'
import { supabase } from '../lib/supabase'

// Always use /api - works with both local Vite proxy and production nginx proxy
export const API_ENDPOINT = '/api';

const api = axios.create({
  baseURL: API_ENDPOINT
})

// Add auth interceptor to include JWT token
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession()
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`
  }
  return config
})

export const uploadDocument = async (file: File): Promise<Document> => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.document
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to upload document'
    throw new Error(message)
  }
}

export const getDocuments = async (): Promise<Document[]> => {
  try {
    const response = await api.get('/documents/')
    return response.data.documents
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch documents'
    throw new Error(message)
  }
}

export const getDocument = async (id: string): Promise<Document> => {
  try {
    const response = await api.get(`/documents/${id}`)
    return response.data.document
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch document'
    throw new Error(message)
  }
}

export const getDocumentStatus = async (id: string): Promise<Document> => {
  try {
    const response = await api.get(`/documents/${id}`)
    return response.data.document
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch document status'
    throw new Error(message)
  }
}

export const analyzeDocument = async (id: string): Promise<Analysis> => {
  try {
    const response = await api.post(`/documents/${id}/analyze`)
    return response.data.analysis
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to analyze document'
    throw new Error(message)
  }
}

export const getDocumentAnalysis = async (id: string): Promise<any> => {
  try {
    const response = await api.get(`/documents/${id}/analysis`)
    return response.data
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch analysis'
    throw new Error(message)
  }
}

export const getSpecificAnalysis = async (documentId: string, iepId: string): Promise<any> => {
  try {
    const response = await api.get(`/documents/${documentId}/analysis/${iepId}`)
    return response.data
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch specific analysis'
    throw new Error(message)
  }
}

export const deleteDocument = async (id: string): Promise<void> => {
  try {
    await api.delete(`/documents/${id}`)
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to delete document'
    throw new Error(message)
  }
}

// Student API functions
export const getStudents = async (): Promise<Student[]> => {
  try {
    const response = await api.get('/students/')
    return response.data.students
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch students'
    throw new Error(message)
  }
}

export const getStudent = async (id: string): Promise<Student> => {
  try {
    const response = await api.get(`/students/${id}`)
    return response.data.student
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to fetch student'
    throw new Error(message)
  }
}

export const createStudent = async (studentData: CreateStudentRequest): Promise<Student> => {
  try {
    const response = await api.post('/students/', studentData)
    return response.data.student
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to create student'
    throw new Error(message)
  }
}

export const updateStudent = async (id: string, studentData: UpdateStudentRequest): Promise<Student> => {
  try {
    const response = await api.put(`/students/${id}`, studentData)
    return response.data.student
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to update student'
    throw new Error(message)
  }
}

export const deleteStudent = async (id: string): Promise<void> => {
  try {
    await api.delete(`/students/${id}`)
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to delete student'
    throw new Error(message)
  }
}

export const linkDocumentToStudent = async (studentId: string, documentId: string): Promise<void> => {
  try {
    await api.post(`/students/${studentId}/link-document/${documentId}`)
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to link document to student'
    throw new Error(message)
  }
}

export const unlinkDocumentFromStudent = async (studentId: string, documentId: string): Promise<void> => {
  try {
    await api.delete(`/students/${studentId}/unlink-document/${documentId}`)
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to unlink document from student'
    throw new Error(message)
  }
}

export const searchStudents = async (query: string): Promise<Student[]> => {
  try {
    const response = await api.get(`/students/search/${encodeURIComponent(query)}`)
    return response.data.students
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to search students'
    throw new Error(message)
  }
}

export const mergeStudents = async (targetStudentId: string, sourceStudentId: string): Promise<any> => {
  try {
    const response = await api.post('/students/merge', {
      target_student_id: targetStudentId,
      source_student_id: sourceStudentId
    })
    return response.data
  } catch (error: any) {
    const message = error.response?.data?.detail || 'Failed to merge students'
    throw new Error(message)
  }
}